/**
 * PlayerList Query Configuration Types
 *
 * These types define the query configuration schema for the PlayerList CMS component,
 * which allows admins to build complex player statistics queries with aggregated stats.
 */

// ============================================================================
// Enums
// ============================================================================

/**
 * Data source collections for statistics
 */
export const StatSource = {
  Goals: 'goals',
  Matches: 'matches',
  YellowCards: 'yellowCards',
  RedCards: 'redCards',
} as const;
export type StatSource = (typeof StatSource)[keyof typeof StatSource];

/**
 * Types of aggregation operations
 */
export const AggregationType = {
  Count: 'count',
  Sum: 'sum',
  Average: 'average',
  Percentage: 'percentage',
} as const;
export type AggregationType = (typeof AggregationType)[keyof typeof AggregationType];

/**
 * Operators for condition comparisons
 */
export const ConditionOperator = {
  Equals: 'eq',
  NotEquals: 'ne',
  GreaterThan: 'gt',
  GreaterThanOrEqual: 'gte',
  LessThan: 'lt',
  LessThanOrEqual: 'lte',
  In: 'in',
  NotIn: 'nin',
  Between: 'between',
} as const;
export type ConditionOperator = (typeof ConditionOperator)[keyof typeof ConditionOperator];

/**
 * Logical operators for combining conditions
 */
export const LogicalOperator = {
  And: 'and',
  Or: 'or',
} as const;
export type LogicalOperator = (typeof LogicalOperator)[keyof typeof LogicalOperator];

/**
 * Fields available on match records that can be used in conditions or aggregations
 */
export const MatchField = {
  StartingEleven: 'startingEleven',
  Substituted: 'substituted',
  Position: 'position',
  MinutesPlayed: 'minutesPlayed',
  Competition: 'competition',
  Team: 'team',
  AgainstTeam: 'againstTeam',
} as const;
export type MatchField = (typeof MatchField)[keyof typeof MatchField];

/**
 * Common stat IDs used throughout the codebase
 */
export const StatId = {
  Goals: 'goals',
  GoalsScored: 'goalsScored',
  Penalties: 'penalties',
  YellowCards: 'yellowCards',
  RedCards: 'redCards',
  Appearances: 'appearances',
  MinutesPlayed: 'minutesPlayed',
  SubAppearances: 'subAppearances',
  UnusedSub: 'unusedSub',
} as const;
export type StatId = (typeof StatId)[keyof typeof StatId];

// ============================================================================
// Condition Types
// ============================================================================

/**
 * A single condition for filtering data
 */
export type StatCondition = {
  /** Field to filter on (e.g., 'minute', 'phase', 'startingEleven', 'goalType') */
  field: string;
  /** Comparison operator */
  operator: ConditionOperator;
  /** Value(s) to compare against */
  value: string | number | boolean | string[] | number[];
  /** End value for 'between' operator */
  valueEnd?: number;
};

/**
 * Recursive condition group with AND/OR logic
 */
export type ConditionGroup = {
  /** Logical operator for combining conditions */
  operator: LogicalOperator;
  /** Child conditions (can be nested groups) */
  conditions: (StatCondition | ConditionGroup)[];
};

/**
 * Type guard to check if a condition is a group
 */
export function isConditionGroup(condition: StatCondition | ConditionGroup): condition is ConditionGroup {
  return 'conditions' in condition && Array.isArray(condition.conditions);
}

// ============================================================================
// Stat Definition Types
// ============================================================================

/**
 * A single condition for filtering aggregated stat values (HAVING clause)
 */
export type AggregateCondition = {
  /** Comparison operator */
  operator: ConditionOperator;
  /** Value to compare against */
  value: number;
  /** End value for 'between' operator */
  valueEnd?: number;
};

/**
 * Group of aggregate conditions with AND/OR logic (HAVING clause)
 */
export type AggregateFilterGroup = {
  /** Logical operator for combining conditions */
  operator: LogicalOperator;
  /** Child conditions (can be nested groups) */
  conditions: (AggregateCondition | AggregateFilterGroup)[];
};

/**
 * Type guard to check if an aggregate filter item is a group
 */
export function isAggregateFilterGroup(item: AggregateCondition | AggregateFilterGroup): item is AggregateFilterGroup {
  return 'conditions' in item && Array.isArray(item.conditions);
}

/**
 * Definition of a single stat column to display
 */
export type StatDefinition = {
  /** Unique identifier for this stat column */
  id: string;
  /** Display label (e.g., "Goals as Sub", "Early Goals") */
  label: string;
  /** Source collection to aggregate from */
  source: StatSource;
  /** Type of aggregation */
  aggregation: AggregationType;
  /** Field to sum when aggregation is 'sum' (e.g., 'minutesPlayed') */
  sumField?: string;
  /** Optional conditions for filtering before aggregation (WHERE clause) */
  conditions?: ConditionGroup;
  /** Optional filter on the aggregated value (HAVING clause) - e.g., "more than 2 yellow cards" */
  aggregateFilter?: AggregateFilterGroup;
  /** @deprecated Use aggregateFilter instead */
  minValue?: number;
  /** Whether this stat can be sorted in the UI */
  sortable?: boolean;
  /**
   * Count an unused substitute's `player_matches` row as well (`StatSource.Matches` only).
   *
   * Every match-sourced stat otherwise excludes a player who was named on the bench and never
   * came on, because he did not make an appearance and counting him would inflate every
   * appearance total on the site. A squad, though, is who was *named*, not who played — so the
   * Squad tab asks for one extra stat carrying this flag purely to widen the set of players the
   * query returns, and leaves its visible Appearances column excluding them as before.
   */
  includeUnusedSubstitutes?: boolean;
};

/**
 * Stat id for the squad-membership column described on `includeUnusedSubstitutes`.
 *
 * Requested by both the club page's Squad tab and search's squad expansion, and never rendered by
 * either — a player list returns everyone who produced a row for *any* requested stat, so this
 * exists only to pull named substitutes into that set. Shared because the two callers live in
 * different packages and exist specifically so they cannot disagree about who is in a squad.
 */
export const SQUAD_MEMBERSHIP_STAT_ID = 'squadMembership';

// ============================================================================
// Filter Types
// ============================================================================

/**
 * Player-level filters applied before aggregation
 */
export type PlayerFilter = {
  /** Filter by player positions (e.g., ['ST', 'CM']) */
  positions?: string[];
  /** Filter by team IDs */
  teams?: string[];
  /** Filter by club IDs (will be converted to team IDs on the backend) */
  clubs?: string[];
  /** Filter by nationality codes (e.g., ['ENG', 'WAL']) */
  nationalities?: string[];
  /** Filter by competition IDs */
  competitions?: string[];
  /** Season filter (e.g., "2024/2025" or "current") */
  season?: string;
  /** Date range filter */
  dateRange?: {
    start?: string;
    end?: string;
  };
};

// ============================================================================
// Main Query Configuration
// ============================================================================

/**
 * Sort configuration for the player list
 */
export type PlayerListSort = {
  /** ID of the stat to sort by */
  statId: string;
  /** Sort order */
  order: 'asc' | 'desc';
};

/**
 * Runtime URL parameter overrides configuration
 */
export type RuntimeOverrides = {
  /** Allow team override via URL param */
  team?: boolean;
  /** Allow competition override via URL param */
  competition?: boolean;
  /** Allow season override via URL param */
  season?: boolean;
};

/**
 * Main query configuration for the PlayerList component
 */
export type PlayerListQueryConfig = {
  /** Player-level filters */
  filters: PlayerFilter;
  /** Stats columns to display and aggregate */
  stats: StatDefinition[];
  /** Default sort configuration */
  defaultSort?: PlayerListSort;
  /** Maximum number of players to return */
  limit: number;
  /** Runtime URL parameter overrides */
  runtimeOverrides?: RuntimeOverrides;
};

// ============================================================================
// Preset Types
// ============================================================================

/**
 * A preset query template
 */
export type PlayerListPreset = {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Description of what this preset shows */
  description: string;
  /** The query configuration */
  config: PlayerListQueryConfig;
};

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Player with computed stats from query
 */
export type PlayerWithStats = {
  id: string;
  name: string;
  position?: string;
  team?: {
    id: string;
    fullName: string;
    logo?: string;
    slug?: string;
    /** Club name variants for display options */
    club?: {
      fullName?: string;
      shortName?: string;
      threeLetterName?: string;
      nickName?: string;
      slug?: string;
      logo?: string;
    };
  };
  /** Competition the player's stats are from (when filtered by competition) */
  competition?: {
    id: string;
    name: string;
    shortName?: string;
  };
  nationality?: string;
  /** Calculated age in years (dateOfBirth is not exposed) */
  age?: number;
  /** Computed stats keyed by stat definition ID */
  stats: Record<string, number>;
};

/**
 * Available fields for conditions based on source
 */
export const AVAILABLE_CONDITION_FIELDS: Record<StatSource, string[]> = {
  [StatSource.Goals]: [
    'minute',
    'phase',
    'goalType',
    'competition',
    'team',
    'againstTeam',
    'playerWasSubstitute',
    'minutesSinceEntry',
  ],
  [StatSource.Matches]: [
    'startingEleven',
    'substituted',
    'position',
    'competition',
    'team',
    'againstTeam',
    'minutesPlayed',
  ],
  [StatSource.YellowCards]: ['minute', 'phase', 'competition', 'team', 'againstTeam'],
  [StatSource.RedCards]: ['minute', 'phase', 'competition', 'team', 'againstTeam'],
};

/**
 * Field metadata for the query builder UI
 */
export type FieldMetadata = {
  name: string;
  label: string;
  type: 'number' | 'boolean' | 'enum' | 'objectId';
  enumValues?: string[];
  description?: string;
};

/**
 * Detailed field metadata by source
 */
export const FIELD_METADATA: Record<StatSource, FieldMetadata[]> = {
  [StatSource.Goals]: [
    { name: 'minute', label: 'Minute', type: 'number', description: 'Goal minute (1-90+)' },
    {
      name: 'phase',
      label: 'Match Phase',
      type: 'enum',
      enumValues: [
        'FirstHalf',
        'SecondHalf',
        'FirstHalfOfExtraTime',
        'SecondHalfOfExtraTime',
        'InjuryTimeOfFirstHalf',
        'InjuryTimeOfSecondHalf',
        'PenaltyShootout',
      ],
    },
    {
      name: 'goalType',
      label: 'Goal Type',
      type: 'enum',
      enumValues: ['DEFAULT', 'PENALTY', 'OWN_GOAL'],
    },
    { name: 'competition', label: 'Competition', type: 'objectId' },
    { name: 'team', label: 'Team', type: 'objectId' },
    { name: 'againstTeam', label: 'Against Team', type: 'objectId' },
    {
      name: 'playerWasSubstitute',
      label: 'Player Was Substitute',
      type: 'boolean',
      description: 'Player came off the bench',
    },
    {
      name: 'minutesSinceEntry',
      label: 'Minutes Since Entry',
      type: 'number',
      description: 'For substitutes, time since entering',
    },
  ],
  [StatSource.Matches]: [
    { name: 'startingEleven', label: 'In Starting XI', type: 'boolean' },
    {
      name: 'substituted',
      label: 'Substituted Off (Minute)',
      type: 'number',
      description: 'Minute when subbed off (null if played full match)',
    },
    {
      name: 'position',
      label: 'Position',
      type: 'enum',
      enumValues: ['GK', 'LB', 'CB', 'RB', 'LW', 'CM', 'CDM', 'CAM', 'RW', 'ST'],
    },
    { name: 'competition', label: 'Competition', type: 'objectId' },
    { name: 'team', label: 'Team', type: 'objectId' },
    { name: 'againstTeam', label: 'Against Team', type: 'objectId' },
    {
      name: 'minutesPlayed',
      label: 'Minutes Played',
      type: 'number',
      description: 'Total minutes played in the match',
    },
  ],
  [StatSource.YellowCards]: [
    { name: 'minute', label: 'Minute', type: 'number' },
    {
      name: 'phase',
      label: 'Match Phase',
      type: 'enum',
      enumValues: [
        'FirstHalf',
        'SecondHalf',
        'FirstHalfOfExtraTime',
        'SecondHalfOfExtraTime',
        'InjuryTimeOfFirstHalf',
        'InjuryTimeOfSecondHalf',
        'PenaltyShootout',
      ],
    },
    { name: 'competition', label: 'Competition', type: 'objectId' },
    { name: 'team', label: 'Team', type: 'objectId' },
    { name: 'againstTeam', label: 'Against Team', type: 'objectId' },
  ],
  [StatSource.RedCards]: [
    { name: 'minute', label: 'Minute', type: 'number' },
    {
      name: 'phase',
      label: 'Match Phase',
      type: 'enum',
      enumValues: [
        'FirstHalf',
        'SecondHalf',
        'FirstHalfOfExtraTime',
        'SecondHalfOfExtraTime',
        'InjuryTimeOfFirstHalf',
        'InjuryTimeOfSecondHalf',
        'PenaltyShootout',
      ],
    },
    { name: 'competition', label: 'Competition', type: 'objectId' },
    { name: 'team', label: 'Team', type: 'objectId' },
    { name: 'againstTeam', label: 'Against Team', type: 'objectId' },
  ],
};
